// ============================================================================
// NEW-VERSION CHECK (Sept 30, 2026)
// ============================================================================
// Why: a single-page app keeps running the code it loaded until the page is
// reloaded. An organizer's dashboard tab left open all day, a player's phone
// app resumed from the home screen, or a clubhouse TV leaderboard can run an
// old copy for days after a deploy. (Found in the Event Pass live test: the
// browser kept showing an unlocked checkbox the server no longer allowed.)
//
// How: the build's main script is named after its content hash
// (/static/js/main.<hash>.js). The server reports the current one at
// GET /api/app-version. If it differs from the one this page loaded, a newer
// version is live:
//   - if nobody has touched the page for 10 minutes, or the tab is coming back
//     after 10+ minutes in the background, reload quietly (nothing is in
//     progress; offline scores are kept in localStorage and survive a reload);
//   - otherwise show a banner with a Refresh button and let the user choose.
//
// Does nothing in development (no hashed main script) or when offline.
// ============================================================================

const CHECK_EVERY_MS = 10 * 60 * 1000; // background poll
const IDLE_MS = 10 * 60 * 1000;        // untouched this long = safe to reload
const RELOADED_KEY = 'tto_reloaded_for';

function currentMain() {
  const el = document.querySelector('script[src*="/static/js/main."]');
  if (!el) return null;
  try {
    return new URL(el.getAttribute('src'), window.location.origin).pathname;
  } catch (e) {
    return null;
  }
}

export function startVersionCheck() {
  const mine = currentMain();
  if (!mine || typeof fetch !== 'function') return;

  let lastInteraction = Date.now();
  let hiddenSince = null;
  let bannerShown = false;
  let checking = false;

  ['pointerdown', 'keydown', 'input', 'touchstart'].forEach((evt) =>
    window.addEventListener(evt, () => { lastInteraction = Date.now(); }, { passive: true, capture: true })
  );

  function reload(target) {
    // One quiet reload per new version per tab: if the reload somehow still
    // serves the old copy, fall back to the banner instead of looping.
    try {
      if (sessionStorage.getItem(RELOADED_KEY) === target) { showBanner(); return; }
      sessionStorage.setItem(RELOADED_KEY, target);
    } catch (e) { /* storage blocked: reload anyway */ }
    window.location.reload();
  }

  function showBanner() {
    if (bannerShown) return;
    bannerShown = true;
    const bar = document.createElement('div');
    bar.setAttribute('role', 'status');
    bar.style.cssText = [
      'position:fixed', 'left:12px', 'right:12px', 'bottom:12px', 'z-index:2147483647',
      'margin:0 auto', 'max-width:560px', 'box-sizing:border-box',
      'background:#14532d', 'color:#ffffff', 'border-radius:10px',
      'box-shadow:0 6px 24px rgba(0,0,0,0.25)', 'padding:12px 14px',
      'display:flex', 'align-items:center', 'gap:12px',
      'font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif', 'font-size:16px', 'line-height:1.35'
    ].join(';');

    const text = document.createElement('div');
    text.style.cssText = 'flex:1';
    text.textContent = 'A new version of Tee Time Organizer is available.';

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = 'Refresh';
    btn.style.cssText = 'background:#ffffff;color:#14532d;border:none;border-radius:8px;padding:10px 16px;font-size:16px;font-weight:700;cursor:pointer;flex-shrink:0';
    btn.addEventListener('click', () => window.location.reload());

    const close = document.createElement('button');
    close.type = 'button';
    close.setAttribute('aria-label', 'Dismiss');
    close.textContent = '×';
    close.style.cssText = 'background:transparent;color:#ffffff;border:none;font-size:24px;line-height:1;padding:4px 6px;cursor:pointer;flex-shrink:0';
    close.addEventListener('click', () => { bar.remove(); });

    bar.appendChild(text);
    bar.appendChild(btn);
    bar.appendChild(close);
    document.body.appendChild(bar);
  }

  async function check(returningAfterMs) {
    if (checking) return;
    checking = true;
    try {
      const res = await fetch('/api/app-version', { cache: 'no-store', credentials: 'omit' });
      if (!res.ok) return;
      const data = await res.json();
      const live = data && data.main;
      if (!live || live === mine) return;

      const idle = Date.now() - lastInteraction >= IDLE_MS;
      const wasAway = returningAfterMs != null && returningAfterMs >= IDLE_MS;
      if (idle || wasAway) reload(live);
      else showBanner();
    } catch (e) {
      // offline or server restarting: try again next time
    } finally {
      checking = false;
    }
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      hiddenSince = Date.now();
    } else {
      const away = hiddenSince ? Date.now() - hiddenSince : null;
      hiddenSince = null;
      check(away);
    }
  });

  setInterval(() => {
    if (document.visibilityState === 'visible') check(null);
  }, CHECK_EVERY_MS);

  // First look shortly after load (catches a page restored from the back/forward cache).
  window.addEventListener('pageshow', (e) => { if (e.persisted) check(IDLE_MS); });
}
